// New-agent onboarding — a guided wizard that gathers what the back
// office needs: contact details, W-9 tax info, direct deposit, then the
// three e-signed documents. Each step validates before it advances and
// autosaves, so a half-finished session picks up where it left off.
//
// Nothing here blocks access to the app, for anyone. It used to, for
// anyone invited with onboarding.required — and a new agent's first
// screen was a demand for their SSN and bank account. What the packet
// actually gates is the money: accounting cannot pay a commission to
// someone with no W-9 and no account on file, and that is where the
// requirement belongs.
import { mimeForFile, assertSupportedDocument, safeDocumentFileName } from '../utils/documentUploads.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Clock, ExternalLink, FileSignature, Send, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/useAuth';
import { LogoIcon } from '../context/TenantContext';
import { supabase } from '../lib/supabase';
import { isOnboardingComplete, REQUIRED_ONBOARDING_DOCS } from '../utils/onboardingStatus';
import { defaultSignedInPath } from '../utils/accessControl';

const DOC_META = {
  contractor: {
    label: 'Contractor Agreement',
    title: 'Independent Contractor Agreement',
    fileSuffix: 'Contractor_Agreement',
    generator: 'generateContractorAgreementPDF',
    detail: 'Your independent contractor agreement, generated from the details you entered.',
  },
  w9: {
    label: 'Form W-9',
    title: 'IRS Form W-9 - Request for Taxpayer Identification Number',
    fileSuffix: 'W9',
    generator: 'generateW9PDF',
    detail: 'Pre-filled from your tax step — review, sign, and it files itself.',
  },
  directDeposit: {
    label: 'Direct Deposit Authorization',
    title: 'Direct Deposit Authorization',
    fileSuffix: 'Direct_Deposit',
    generator: 'generateDirectDepositPDF',
    detail: 'Authorizes commission payouts to the bank account you entered.',
  },
};

const blankContact = { phone: '', address: '', city: '', state: '', zip: '', dob: '', tinLast4: '' };
const blankDeposit = { bankName: '', accountHolderName: '', accountType: 'Checking', routingNumber: '', accountNumber: '' };
const blankW9 = { taxClassification: 'individual', businessName: '', tinFull: '', tinType: 'ssn' };
const W9_CLASSIFICATIONS = [
  ['individual', 'Individual / Sole Proprietor'],
  ['c_corp', 'C Corporation'],
  ['s_corp', 'S Corporation'],
  ['partnership', 'Partnership'],
  ['trust', 'Trust / Estate'],
  ['llc', 'LLC'],
  ['other', 'Other'],
];

const STEPS = [
  { key: 'contact', label: 'Your Details' },
  { key: 'tax', label: 'Tax / W-9' },
  { key: 'deposit', label: 'Direct Deposit' },
  { key: 'docs', label: 'Sign Documents' },
];

function safeName(name) {
  return String(name || 'User').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 90) || 'User';
}

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

// ABA routing checksum — catches transposed digits before a bad
// account ever reaches accounting.
function validRoutingNumber(value) {
  const d = digitsOnly(value);
  if (d.length !== 9) return false;
  const n = d.split('').map(Number);
  return ((3 * (n[0] + n[3] + n[6])) + (7 * (n[1] + n[4] + n[7])) + (n[2] + n[5] + n[8])) % 10 === 0;
}

function validateContact(contact) {
  const errors = {};
  if (digitsOnly(contact.phone).length < 10) errors.phone = 'Enter a 10-digit phone number.';
  if (!String(contact.address || '').trim()) errors.address = 'Street address is required.';
  if (!String(contact.city || '').trim()) errors.city = 'City is required.';
  if (!/^[A-Za-z]{2}$/.test(String(contact.state || '').trim())) errors.state = 'Use the 2-letter state code.';
  if (!/^\d{5}(-\d{4})?$/.test(String(contact.zip || '').trim())) errors.zip = 'Enter a 5-digit ZIP.';
  return errors;
}

function validateTax(w9Info, onboarding) {
  const errors = {};
  if (!w9Info.taxClassification) errors.taxClassification = 'Choose a tax classification.';
  if (w9Info.taxClassification !== 'individual' && !String(w9Info.businessName || '').trim()) {
    errors.businessName = 'Business name is required for this classification.';
  }
  const tinDigits = digitsOnly(w9Info.tinFull);
  const tinOnFile = !!onboarding?.w9Info?.tinLast4;
  if (!tinDigits && !tinOnFile) {
    errors.tinFull = w9Info.tinType === 'ein' ? 'Enter your 9-digit EIN.' : 'Enter your 9-digit SSN.';
  } else if (tinDigits && tinDigits.length !== 9) {
    errors.tinFull = 'A TIN is exactly 9 digits.';
  }
  return errors;
}

function validateDeposit(deposit, confirmAccount) {
  const errors = {};
  if (!String(deposit.bankName || '').trim()) errors.bankName = 'Bank name is required.';
  if (!String(deposit.accountHolderName || '').trim()) errors.accountHolderName = 'Account holder name is required.';
  if (!validRoutingNumber(deposit.routingNumber)) errors.routingNumber = 'Enter a valid 9-digit ABA routing number.';
  const acct = digitsOnly(deposit.accountNumber);
  if (acct.length < 4 || acct.length > 17) errors.accountNumber = 'Account numbers are 4–17 digits.';
  else if (digitsOnly(confirmAccount) !== acct) errors.confirmAccount = 'Account numbers do not match.';
  return errors;
}

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const [onboarding, setOnboarding] = useState(user?.onboarding || {});
  const [contact, setContact] = useState(blankContact);
  const [deposit, setDeposit] = useState(blankDeposit);
  const [confirmAccount, setConfirmAccount] = useState('');
  const [w9Info, setW9Info] = useState(blankW9);
  const [step, setStep] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyKind, setBusyKind] = useState(null);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [error, setError] = useState('');

  const complete = useMemo(() => isOnboardingComplete(onboarding), [onboarding]);
  const signedCount = REQUIRED_ONBOARDING_DOCS.filter((kind) => onboarding?.[kind]?.status === 'signed').length;

  const reloadRef = useRef(() => {});
  const reload = useCallback(async () => {
    if (!user?.id) return;
    const { data, error: loadErr } = await supabase
      .from('profiles')
      .select('onboarding')
      .eq('id', user.id)
      .maybeSingle();
    if (loadErr) setError(loadErr.message);
    const next = data?.onboarding || {};
    setOnboarding(next);
    setContact((prev) => ({ ...blankContact, ...(next.contactInfo || {}), phone: next.contactInfo?.phone || prev.phone || user.phone || '' }));
    setDeposit((prev) => {
      const merged = { ...blankDeposit, ...(next.directDepositInfo || {}) };
      setConfirmAccount((cur) => cur || merged.accountNumber || '');
      return { ...merged, accountHolderName: merged.accountHolderName || prev.accountHolderName || user.name || '' };
    });
    setW9Info((prev) => ({ ...blankW9, ...(next.w9Info || {}), tinFull: prev.tinFull || '' }));
    setLoading(false);
    return next;
  }, [user?.id, user?.phone, user?.name]);
  reloadRef.current = reload;

  useEffect(() => {
    setLoading(true);
    reloadRef.current();
  }, [user?.id]);

  // Signatures land out-of-band (the e-sign tab, or a phone). While
  // any packet doc is out for signature, refresh on window focus and
  // every 20s so the checklist ticks itself without a manual reload.
  useEffect(() => {
    const anySent = REQUIRED_ONBOARDING_DOCS.some((kind) => onboarding?.[kind]?.status === 'sent');
    if (!anySent || complete) return undefined;
    const timer = setInterval(() => { reloadRef.current(); }, 20000);
    const onFocus = () => { reloadRef.current(); };
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [onboarding, complete]);

  useEffect(() => {
    if (!complete) return undefined;
    const t = setTimeout(() => {
      refreshUser?.().finally(() => {
        // The same landing rule as every other way in. This was a
        // hard-coded '/deals', so the one person who has never seen the
        // app — someone finishing onboarding — was the one person who
        // never saw its home page.
        window.location.href = defaultSignedInPath(user);
      });
    }, 4400);
    return () => clearTimeout(t);
  }, [complete, refreshUser, user]);

  const saveInfo = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const cleanTinFull = String(w9Info.tinFull || '').replace(/[^0-9-]/g, '').trim();
      const next = {
        ...onboarding,
        contactInfo: { ...contact, submittedAt: new Date().toISOString() },
        directDepositInfo: { ...deposit, submittedAt: new Date().toISOString() },
        w9Info: {
          taxClassification: w9Info.taxClassification,
          businessName: w9Info.businessName,
          tinType: w9Info.tinType,
          tinLast4: cleanTinFull.replace(/[^0-9]/g, '').slice(-4) || onboarding?.w9Info?.tinLast4 || contact.tinLast4 || '',
          submittedAt: new Date().toISOString(),
        },
      };
      const { error: saveErr } = await supabase.from('profiles').update({ onboarding: next }).eq('id', user.id);
      if (saveErr) throw saveErr;
      // The full TIN never sits in the profile JSON — it goes to the
      // service-role-only tax table and only the last 4 stay visible.
      if (cleanTinFull) {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const taxRes = await fetch('/api/onboarding/tax-info', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            taxClassification: w9Info.taxClassification,
            businessName: w9Info.businessName,
            tinFull: cleanTinFull,
          }),
        });
        const taxBody = await taxRes.json().catch(() => ({}));
        if (!taxRes.ok) throw new Error(taxBody?.error || `Tax info save failed (${taxRes.status})`);
      }
      setOnboarding(next);
      await refreshUser?.();
      return true;
    } catch (err) {
      setError(err?.message || 'Could not save onboarding info.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [contact, deposit, onboarding, refreshUser, user?.id, w9Info]);

  const stepErrors = useCallback((index) => {
    if (index === 0) return validateContact(contact);
    if (index === 1) return validateTax(w9Info, onboarding);
    if (index === 2) return validateDeposit(deposit, confirmAccount);
    return {};
  }, [contact, w9Info, onboarding, deposit, confirmAccount]);

  const stepDone = (index) => {
    if (index === 3) return complete;
    return Object.keys(stepErrors(index)).length === 0;
  };

  const goToStep = (index) => {
    // Backward is always free; forward requires every earlier step valid.
    if (index <= step) {
      setFieldErrors({});
      setStep(index);
      return;
    }
    for (let i = 0; i < index; i += 1) {
      const errors = stepErrors(i);
      if (Object.keys(errors).length) {
        setStep(i);
        setFieldErrors(errors);
        return;
      }
    }
    setFieldErrors({});
    setStep(index);
  };

  const continueStep = async () => {
    const errors = stepErrors(step);
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    const saved = await saveInfo();
    if (!saved) return;
    setStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const sendForSignature = async (kind) => {
    const meta = DOC_META[kind];
    if (!meta || busyKind) return;
    setBusyKind(kind);
    setError('');
    try {
      await saveInfo();
      const docs = await import('../utils/generateOnboardingDocs');
      const pdfDoc = await docs[meta.generator]({ user, contact, terms: {}, taxInfo: w9Info, directDeposit: deposit });
      const pdfBase64 = pdfDoc.output('datauristring').split(',')[1];
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch('/api/sign/send', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          onboardingUserId: user.id,
          onboardingKind: kind,
          pdfBase64,
          fileName: `${safeName(user.name || user.email)}_${meta.fileSuffix}.pdf`,
          title: meta.title,
          subject: `Please sign: ${meta.title}`,
          message: `Please review and e-sign the attached ${meta.title}. Accounting needs it on file before your first commission can be paid.`,
          signerName: user.name || user.email,
          signerEmail: user.email,
          signerPhone: contact.phone || user.phone || undefined,
          deliveryChannels: 'email',
          createdByUserId: user.id,
          createdByName: user.name || user.email,
          createdByEmail: user.email,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      await reload();
      await refreshUser?.();
      if (body?.signerUrl) window.open(body.signerUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err?.message || 'Could not start e-sign request.');
    } finally {
      setBusyKind(null);
    }
  };

  const uploadExecutedDoc = async (kind, file) => {
    const meta = DOC_META[kind];
    if (!meta || !file || uploadingKind) return;
    setUploadingKind(kind);
    setError('');
    try {
      await saveInfo();
      const tenantId = user?.tenantId;
      if (!tenantId) throw new Error('Your profile is missing a tenant id. Ask an admin to refresh your user profile before uploading onboarding documents.');
      const mime = mimeForFile(file);
      assertSupportedDocument({ mime, size: file?.size || 0, name: file?.name || kind });
      const id = `onboarding-${user.id}-${kind}-${Date.now()}`;
      const path = `tenants/${tenantId}/onboarding/${user.id}/${kind}/${Date.now()}-${safeDocumentFileName(file.name, kind)}`;
      const { data: uploaded, error: uploadErr } = await supabase.storage.from('documents').upload(path, file, {
        upsert: false,
        contentType: mime,
      });
      if (uploadErr) throw uploadErr;
      const { error: docErr } = await supabase.from('documents').insert({
        id,
        name: file.name,
        doc_type: `Signed ${kind}`,
        instrument_title: meta.title,
        storage_path: uploaded?.path || path,
        file_size: file.size || null,
        source: 'onboarding_upload',
        original_filename: file.name,
        uploaded_by: user.id,
      });
      if (docErr) throw docErr;
      const next = {
        ...onboarding,
        [kind]: {
          ...(onboarding[kind] || {}),
          status: 'signed',
          signedAt: new Date().toISOString(),
          signedDocumentId: id,
          uploadedOutsideApp: true,
          fileName: file.name,
        },
      };
      const { error: profileErr } = await supabase.from('profiles').update({ onboarding: next }).eq('id', user.id);
      if (profileErr) throw profileErr;
      setOnboarding(next);
      await refreshUser?.();
    } catch (err) {
      setError(err?.message || 'Could not upload executed onboarding document.');
    } finally {
      setUploadingKind(null);
    }
  };

  if (loading) {
    return <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', color: '#64748b' }}>Loading onboarding…</div>;
  }

  if (complete) {
    return <WelcomeComplete user={user} />;
  }

  const err = (key) => fieldErrors[key] || '';

  return (
    <div style={{
      minHeight: '100vh',
      width: '100vw',
      color: '#0f172a',
      padding: '2rem',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#071a38',
      backgroundImage: `
        linear-gradient(rgba(91,191,239,0.12) 1px, transparent 1px),
        linear-gradient(90deg, rgba(91,191,239,0.12) 1px, transparent 1px),
        radial-gradient(circle at 50% 0%, rgba(91,191,239,0.18), transparent 42%)
      `,
      backgroundSize: '32px 32px, 32px 32px, 100% 100%',
      backgroundPosition: 'center center',
    }}>
      <div style={{ width: 'min(880px, 100%)', maxHeight: 'calc(100vh - 4rem)', overflowY: 'auto', background: '#f8fafc', border: '1px solid rgba(91,191,239,0.45)', boxShadow: '0 28px 80px rgba(0,0,0,0.42)' }}>
        <div style={{ background: 'linear-gradient(135deg, #0c1e3f 0%, #003279 100%)', color: '#fff', padding: '1.25rem 1.5rem', display: 'flex', gap: 14, alignItems: 'center' }}>
          <div style={{ width: 42, height: 42, display: 'grid', placeItems: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
            <ShieldCheck size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.68rem', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#a5c4f1', fontWeight: 600 }}>
              Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
            </div>
            <h1 style={{ margin: 0, fontSize: '1.25rem' }}>Set up your Paraclete account</h1>
            <p style={{ margin: '0.25rem 0 0', color: '#cbd5e1', fontSize: '0.85rem' }}>
              Four quick steps — your details, tax info, direct deposit, then three e-signatures.
              Finish them whenever suits; the app is already open to you, and this is what lets
              accounting pay you.
            </p>
          </div>
          <div style={{ textAlign: 'right', fontSize: '0.72rem', color: '#a5c4f1', fontWeight: 600 }}>
            {signedCount}/{REQUIRED_ONBOARDING_DOCS.length} signed
          </div>
        </div>

        {/* Step rail — click back any time; forward only through valid steps. */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
          {STEPS.map((s, index) => {
            const active = index === step;
            const done = stepDone(index) && index !== step;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => goToStep(index)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '0.7rem 0.4rem',
                  border: 'none',
                  borderBottom: active ? '3px solid #1d4ed8' : '3px solid transparent',
                  background: 'transparent',
                  color: active ? '#1d4ed8' : done ? '#047857' : '#64748b',
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                }}
              >
                {done ? <CheckCircle2 size={13} /> : <Clock size={13} style={{ opacity: active ? 1 : 0.55 }} />}
                {index + 1}. {s.label}
              </button>
            );
          })}
        </div>

        <div style={{ padding: '1.25rem', display: 'grid', gap: '1rem' }}>
          {error && <div style={{ padding: '0.75rem 0.9rem', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontWeight: 600 }}>{error}</div>}

          {step === 0 && (
            <section style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '1rem' }}>
              <h2 style={{ margin: '0 0 0.35rem', fontSize: '0.95rem' }}>Your Details</h2>
              <p style={{ margin: '0 0 0.75rem', color: '#64748b', fontSize: '0.76rem' }}>
                Used on your contractor agreement and payout records. Everything here stays inside Paraclete.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                <Field label="Phone" required value={contact.phone} error={err('phone')} onChange={(v) => setContact((p) => ({ ...p, phone: v }))} />
                <Field label="Date of Birth" type="date" value={contact.dob} onChange={(v) => setContact((p) => ({ ...p, dob: v }))} />
                <Field label="Street Address" required value={contact.address} error={err('address')} onChange={(v) => setContact((p) => ({ ...p, address: v }))} wide />
                <Field label="City" required value={contact.city} error={err('city')} onChange={(v) => setContact((p) => ({ ...p, city: v }))} />
                <Field label="State" required value={contact.state} error={err('state')} onChange={(v) => setContact((p) => ({ ...p, state: v.toUpperCase().slice(0, 2) }))} />
                <Field label="ZIP" required value={contact.zip} error={err('zip')} onChange={(v) => setContact((p) => ({ ...p, zip: v }))} />
              </div>
            </section>
          )}

          {step === 1 && (
            <section style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '1rem' }}>
              <h2 style={{ margin: '0 0 0.35rem', fontSize: '0.95rem' }}>Tax / W-9</h2>
              <p style={{ margin: '0 0 0.75rem', color: '#64748b', fontSize: '0.76rem' }}>
                These fields fill your W-9 PDF before it goes out for e-sign. Your full TIN is stored in a restricted
                tax table — only the last four digits ever show in the app.
                {onboarding?.w9Info?.tinLast4 ? ` A TIN ending ••••${onboarding.w9Info.tinLast4} is already on file — re-enter only to change it.` : ''}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                <SelectField
                  label="Federal Tax Classification"
                  value={w9Info.taxClassification}
                  onChange={(v) => setW9Info((p) => ({ ...p, taxClassification: v }))}
                  options={W9_CLASSIFICATIONS}
                />
                <SelectField
                  label="TIN Type"
                  value={w9Info.tinType}
                  onChange={(v) => setW9Info((p) => ({ ...p, tinType: v }))}
                  options={[['ssn', 'SSN'], ['ein', 'EIN']]}
                />
                <Field
                  label="Business / DBA Name"
                  required={w9Info.taxClassification !== 'individual'}
                  value={w9Info.businessName}
                  error={err('businessName')}
                  onChange={(v) => setW9Info((p) => ({ ...p, businessName: v }))}
                />
                <Field
                  label={w9Info.tinType === 'ein' ? 'EIN (9 digits)' : 'SSN (9 digits)'}
                  required={!onboarding?.w9Info?.tinLast4}
                  value={w9Info.tinFull}
                  error={err('tinFull')}
                  onChange={(v) => setW9Info((p) => ({ ...p, tinFull: v }))}
                />
              </div>
            </section>
          )}

          {step === 2 && (
            <section style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '1rem' }}>
              <h2 style={{ margin: '0 0 0.35rem', fontSize: '0.95rem' }}>Direct Deposit</h2>
              <p style={{ margin: '0 0 0.75rem', color: '#64748b', fontSize: '0.76rem' }}>
                Where your commission payouts land. The routing number is checksum-verified so a typo can't
                reach accounting.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                <Field label="Bank Name" required value={deposit.bankName} error={err('bankName')} onChange={(v) => setDeposit((p) => ({ ...p, bankName: v }))} />
                <Field label="Account Holder Name" required value={deposit.accountHolderName} error={err('accountHolderName')} onChange={(v) => setDeposit((p) => ({ ...p, accountHolderName: v }))} />
                <SelectField
                  label="Account Type"
                  value={deposit.accountType}
                  onChange={(v) => setDeposit((p) => ({ ...p, accountType: v }))}
                  options={[['Checking', 'Checking'], ['Savings', 'Savings']]}
                />
                <Field label="Routing Number" required value={deposit.routingNumber} error={err('routingNumber')} onChange={(v) => setDeposit((p) => ({ ...p, routingNumber: v }))} />
                <Field label="Account Number" required value={deposit.accountNumber} error={err('accountNumber')} onChange={(v) => setDeposit((p) => ({ ...p, accountNumber: v }))} />
                <Field label="Confirm Account Number" required value={confirmAccount} error={err('confirmAccount')} onChange={setConfirmAccount} />
              </div>
            </section>
          )}

          {step === 3 && (
            <section style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '1rem' }}>
              <h2 style={{ margin: '0 0 0.35rem', fontSize: '0.95rem' }}>Sign Your Documents</h2>
              <p style={{ margin: '0 0 0.75rem', color: '#64748b', fontSize: '0.76rem' }}>
                Each document is generated from the information you just entered. Start the e-sign and a signing
                link opens here (and lands in your email). Already signed on paper? Upload the executed copy
                instead. This page updates itself as signatures come in.
              </p>
              <div style={{ display: 'grid', gap: 8 }}>
                {REQUIRED_ONBOARDING_DOCS.map((kind) => {
                  const meta = DOC_META[kind];
                  const status = onboarding?.[kind]?.status || 'not_started';
                  const signed = status === 'signed';
                  const sent = status === 'sent';
                  return (
                    <div key={kind} style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #e2e8f0', padding: '0.75rem', background: signed ? '#f0fdf4' : '#fff' }}>
                      <div style={{ width: 34, height: 34, display: 'grid', placeItems: 'center', background: signed ? '#dcfce7' : '#eff6ff', color: signed ? '#047857' : '#1d4ed8' }}>
                        {signed ? <CheckCircle2 size={17} /> : <FileSignature size={17} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{meta.label}</div>
                        <div style={{ color: '#64748b', fontSize: '0.74rem' }}>
                          {signed
                            ? `Signed ${new Date(onboarding[kind].signedAt).toLocaleDateString()}`
                            : sent
                              ? 'Signing link sent — check your email, or reopen it here.'
                              : meta.detail}
                        </div>
                      </div>
                      <button onClick={() => sendForSignature(kind)} disabled={busyKind === kind || signed} style={primaryButton(signed ? '#94a3b8' : '#1d4ed8')}>
                        {signed ? <CheckCircle2 size={13} /> : sent ? <ExternalLink size={13} /> : <Send size={13} />}
                        {busyKind === kind ? 'Preparing…' : signed ? 'Signed' : sent ? 'Open / Resend' : 'Start E-Sign'}
                      </button>
                      {!signed && (
                        <label style={{ ...primaryButton('#475569'), cursor: uploadingKind === kind ? 'wait' : 'pointer' }}>
                          {uploadingKind === kind ? 'Uploading…' : 'Upload Signed'}
                          <input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg"
                            disabled={uploadingKind === kind}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = '';
                              if (file) uploadExecutedDoc(kind, file);
                            }}
                            style={{ display: 'none' }}
                          />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
              <p style={{ margin: '0.85rem 0 0', color: '#64748b', fontSize: '0.74rem' }}>
                Stuck on a document? Email your admin — they can see your onboarding status and help from Settings → Team.
              </p>
            </section>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => goToStep(Math.max(step - 1, 0))}
              disabled={step === 0}
              style={primaryButton(step === 0 ? '#94a3b8' : '#475569')}
            >
              Back
            </button>
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={continueStep} disabled={saving} style={primaryButton('#1d4ed8')}>
                {saving ? 'Saving…' : 'Save & Continue'}
              </button>
            ) : (
              <button type="button" onClick={() => reloadRef.current()} style={primaryButton('#475569')}>
                Refresh Status
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function WelcomeComplete({ user }) {
  return (
    <div style={{
      minHeight: '100vh',
      width: '100vw',
      color: '#ffffff',
      padding: '2rem',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: '#071a38',
      backgroundImage: `
        linear-gradient(rgba(91,191,239,0.13) 1px, transparent 1px),
        linear-gradient(90deg, rgba(91,191,239,0.13) 1px, transparent 1px),
        radial-gradient(circle at 50% 12%, rgba(91,191,239,0.24), transparent 44%)
      `,
      backgroundSize: '32px 32px, 32px 32px, 100% 100%',
      backgroundPosition: 'center center',
    }}>
      <style>{`
        @keyframes welcomeRise {
          from { opacity: 0; transform: translateY(18px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes welcomeRing {
          0% { transform: rotate(0deg) scale(1); opacity: 0.55; }
          50% { transform: rotate(180deg) scale(1.04); opacity: 0.95; }
          100% { transform: rotate(360deg) scale(1); opacity: 0.55; }
        }
        @keyframes welcomePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(91,191,239,0.42); }
          50% { box-shadow: 0 0 0 18px rgba(91,191,239,0); }
        }
        @keyframes welcomeSweep {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        @keyframes welcomeGridGlow {
          0%, 100% { opacity: 0.35; transform: translateY(0); }
          50% { opacity: 0.7; transform: translateY(-10px); }
        }
      `}</style>
      <div style={{
        position: 'absolute',
        inset: '8%',
        border: '1px solid rgba(91,191,239,0.16)',
        animation: 'welcomeGridGlow 3.2s ease-in-out infinite',
        pointerEvents: 'none',
      }} />
      <div style={{
        width: 'min(720px, 100%)',
        background: 'linear-gradient(180deg, rgba(12,30,63,0.96) 0%, rgba(0,50,121,0.94) 100%)',
        border: '1px solid rgba(91,191,239,0.5)',
        boxShadow: '0 30px 90px rgba(0,0,0,0.48), inset 0 1px 0 rgba(255,255,255,0.08)',
        padding: '3rem 2.5rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        animation: 'welcomeRise 700ms ease-out both',
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(115deg, transparent 0%, rgba(255,255,255,0.08) 42%, transparent 62%)',
          transform: 'translateX(-100%)',
          animation: 'welcomeSweep 1.5s ease-out 350ms both',
          pointerEvents: 'none',
        }} />
        <div style={{
          width: 118,
          height: 118,
          margin: '0 auto 1.25rem',
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
        }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            border: '1px solid rgba(91,191,239,0.45)',
            borderTopColor: '#ffffff',
            animation: 'welcomeRing 2.6s linear infinite',
          }} />
          <div style={{
            width: 88,
            height: 88,
            display: 'grid',
            placeItems: 'center',
            position: 'relative',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(219,234,254,0.94) 100%)',
            border: '1px solid rgba(255,255,255,0.78)',
            boxShadow: '0 16px 38px rgba(0,0,0,0.28)',
            animation: 'welcomePulse 1.8s ease-in-out infinite',
          }}>
            <LogoIcon size={54} className="" />
            <span style={{
              position: 'absolute',
              right: -5,
              bottom: -5,
              width: 30,
              height: 30,
              display: 'grid',
              placeItems: 'center',
              background: '#16a34a',
              border: '2px solid #ffffff',
              borderRadius: 999,
              boxShadow: '0 10px 22px rgba(0,0,0,0.25)',
            }}>
              <CheckCircle2 size={18} color="#ffffff" strokeWidth={2.4} />
            </span>
          </div>
        </div>
        <div style={{ fontSize: '0.72rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#a5c4f1', fontWeight: 700 }}>
          Onboarding Complete
        </div>
        <h1 style={{ margin: '0.35rem 0 0', fontSize: 'clamp(2rem, 5vw, 3.8rem)', lineHeight: 1, fontWeight: 700, color: '#ffffff' }}>
          Welcome to ParacleteOil
        </h1>
        <p style={{ margin: '1rem auto 0', maxWidth: 520, color: '#dbeafe', fontSize: '1rem', lineHeight: 1.55 }}>
          {user?.name || 'Your account'} is ready. We are opening the Deal Tracker now.
        </p>
        <div style={{
          margin: '1.75rem auto 0',
          height: 6,
          width: 'min(360px, 100%)',
          background: 'rgba(255,255,255,0.12)',
          overflow: 'hidden',
          border: '1px solid rgba(91,191,239,0.25)',
        }}>
          <div style={{
            height: '100%',
            width: '100%',
            background: 'linear-gradient(90deg, #5bbfef 0%, #ffffff 55%, #5bbfef 100%)',
            transform: 'translateX(-100%)',
            animation: 'welcomeSweep 4s linear both',
          }} />
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', wide = false, required = false, error = '' }) {
  return (
    <label style={{ display: 'grid', gap: 4, gridColumn: wide ? '1 / -1' : undefined }}>
      <span style={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600 }}>
        {label}{required ? <span style={{ color: '#dc2626' }}> *</span> : null}
      </span>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={{ border: `1px solid ${error ? '#f87171' : '#cbd5e1'}`, padding: '0.55rem 0.6rem', fontSize: '0.84rem' }}
      />
      {error && <span style={{ fontSize: '0.68rem', color: '#b91c1c', fontWeight: 600 }}>{error}</span>}
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600 }}>{label}</span>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={{ border: '1px solid #cbd5e1', padding: '0.55rem 0.6rem', fontSize: '0.84rem', background: '#fff' }}
      >
        {options.map(([val, labelText]) => <option key={val} value={val}>{labelText}</option>)}
      </select>
    </label>
  );
}

function primaryButton(color, extra = {}) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    border: 'none',
    background: color,
    color: '#fff',
    padding: '0.5rem 0.75rem',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: color === '#94a3b8' ? 'not-allowed' : 'pointer',
    ...extra,
  };
}
